import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import {
  IListEmployee,
  IEmployee,
  IEmployeeExpense,
  IEmployeeWalletBenefit,
  IEmployeeWalletRecognitions,
  IFileImport,
} from 'types/income';
import { TableDataResponse } from 'types/general';
import { ShopItemCategory } from 'types/MarketplaceItems';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { ExpenseStatus } from 'store/expenses';
import {
  exportXlsxConfig,
  mergeOrgStructureIds,
  getBinaryValue,
  getFormData,
  getSorting,
  toCents,
} from 'utils/general';
import {
  Employee,
  EmployeeExpensesFilters,
  EmployeeJobTitlesFilters,
  EmployeeWalletBenefitsFilters,
  EmployeesFilters,
} from './employees.types';

const getFilters = (filters: Partial<EmployeesFilters>) => {
  const orgStructure = mergeOrgStructureIds(filters);

  return {
    page: filters.page,
    pageSize: filters.pageSize,
    ...getSorting(filters.sort),
    search: filters.search || undefined,
    filter: {
      name: filters.name || undefined,
      email: filters.email || undefined,
      phone: filters.phone || undefined,
      employeeIds: filters.employees,
      employeeGroupIds: filters.employeeGroups,
      employmentStatus: filters.status,
      hireDateFrom: filters.hiredAt?.from,
      hireDateTo: filters.hiredAt?.to,
      appUser: getBinaryValue(filters.isAppUser),
      birthdaysSoon: getBinaryValue(filters.birthdaySoon),
      workAnniversarySoon: getBinaryValue(filters.workAnniversarySoon),
      companyIds: orgStructure.companies,
      departmentIds: orgStructure.departments,
      teamIds: orgStructure.teams,
      customCategoryId: filters.customCategory || undefined,
      customCategoryValueIds: filters.customCategoryValues,
      emailConsent: getBinaryValue(filters.emailConsent),
      phoneNumberConsent: getBinaryValue(filters.phoneNumberConsent),
      birthdateConsent: getBinaryValue(filters.birthdateConsent),
    },
  };
};

interface GetEmployees {
  filters: Partial<EmployeesFilters>;
}

interface GetEmployee {
  employeeId: string;
}

interface CreateEmployee {
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  personalCode: string | null;
  companyId: string;
  startDate: string | null;
  startDateTZ: number;
  externalId?: string | null;
}

interface UpdateEmployee {
  employeeId: string;
  data: Partial<
    CreateEmployee & {
      departmentId: string | null;
      teamId: string | null;
      categories: Array<{
        employeeCategoryId: string;
        employeeCategoryValueId: string;
      }>;
      employmentDate: string | null;
    } & Pick<
        Employee,
        | 'dateOfBirth'
        | 'internalComment'
        | 'jobTitle'
        | 'employeeId'
        | 'hireDate'
        | 'capacity'
        | 'endDate'
        | 'endDateTZ'
        | 'employmentDateTZ'
        | 'endTime'
        | 'startTime'
        | 'iban'
        | 'avatarId'
        | 'consents'
      >
  >;
}

interface BulkUpdateEmployees {
  employees: string[];
  data: Partial<
    Pick<
      Employee,
      | 'startDate'
      | 'startDateTZ'
      | 'endDate'
      | 'endDateTZ'
      | 'temporaryEmployment'
      | 'hireDate'
    >
  > & {
    consents?: Partial<Employee['consents']>;
  };
}

interface SendInvitations {
  employees: string[];
}

interface DeleteEmployee {
  employeeId: string;
}

interface GetJobTitles {
  filters: Partial<EmployeeJobTitlesFilters>;
}

interface GetEmployeeExpenses {
  employeeId: string;
  filters: Partial<EmployeeExpensesFilters>;
}

interface CreateExpense {
  employeeId: string;
  expense: {
    date: string;
    status: ExpenseStatus;
    benefits: string[];
    recognition: boolean;
    shopCategories: ShopItemCategory[];
    amount: number;
    description: string;
  };
}

interface GetWalletBenefits {
  employeeId: string;
  filters: Partial<EmployeeWalletBenefitsFilters>;
}

interface GetWalletRecognitions {
  employeeId: string;
}

interface ExportXls {
  filters: Partial<EmployeesFilters>;
}

interface UnlinkEmployee {
  employeeId: string;
}

interface ExportConsents {
  filters: Partial<EmployeesFilters>;
}

interface ImportConsents {
  file: File;
}

interface PrepareEmployeeBlueprint {
  file: File;
  identifierColumns: string;
}

interface ImportEmployeeBlueprint {
  file: File;
}

interface ExportOrgStructureEmployees {
  filters: Partial<EmployeesFilters>;
}

interface ImportOrgStructure {
  file: File;
}

export const loaders = {
  getEmployees: ({ filters }: GetEmployees) => {
    return api<TableDataResponse<IListEmployee>>({
      url: Endpoints.clientAdmin.employees.root,
      version: 'v2',
      params: {
        ...getFilters(filters),
      },
    });
  },
  getEmployee: ({ employeeId }: GetEmployee) => {
    return api<IEmployee>({
      url: Endpoints.clientAdmin.employees.byId.root.replace(
        ':employeeId',
        employeeId,
      ),
    });
  },
  createEmployee: (data: CreateEmployee) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.employees.create,
      data,
      method: 'post',
    });
  },
  updateEmployee: ({ employeeId, data }: UpdateEmployee) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.employees.byId.root.replace(
        ':employeeId',
        employeeId,
      ),
      data,
      method: 'patch',
    });
  },
  bulkUpdateEmployees: ({ employees, data }: BulkUpdateEmployees) => {
    return api({
      url: Endpoints.clientAdmin.employees.bulk.update,
      data: {
        employeeIds: employees,
        employee: data,
      },
      method: 'patch',
    });
  },
  sendInvitations: ({ employees }: SendInvitations) => {
    return api({
      url: Endpoints.clientAdmin.employees.invitations,
      data: {
        employeeIds: employees,
      },
      method: 'post',
    });
  },
  deleteEmployee: ({ employeeId }: DeleteEmployee) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.employees.byId.root.replace(
        ':employeeId',
        employeeId,
      ),
      method: 'delete',
    });
  },
  exportXls: ({ filters }: ExportXls) => {
    const { page, pageSize, sorting, direction, ...restFilters } =
      getFilters(filters);
    return api({
      url: Endpoints.clientAdmin.employees.export.xls,
      params: {
        ...restFilters,
      },
      ...exportXlsxConfig,
    });
  },
  exportEmployeeBlueprint: ({ filters }: ExportXls) => {
    const { page, pageSize, sorting, direction, ...restFilters } =
      getFilters(filters);

    return api({
      url: Endpoints.clientAdmin.employees.export.blueprint.root,
      params: {
        ...restFilters,
      },
      ...exportXlsxConfig,
    });
  },
  getJobTitles: ({ filters }: GetJobTitles) => {
    return api<string[]>({
      url: Endpoints.clientAdmin.employees.jobTitles,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search || undefined,
      },
    });
  },
  getUniqueJobTitles: () => {
    return api<string[]>({
      url: Endpoints.clientAdmin.automations.jobTitles,
    });
  },
  getExpenses: ({ employeeId, filters }: GetEmployeeExpenses) => {
    return api<TableDataResponse<IEmployeeExpense>>({
      url: Endpoints.clientAdmin.employees.byId.expenses.root.replace(
        ':employeeId',
        employeeId,
      ),
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        filter: {
          creationDateFrom: filters.createdAt?.from,
          creationDateTo: filters.createdAt?.to,
          dateFrom: filters.date?.from,
          dateTo: filters.date?.to,
          itemCategory: filters.categories,
          expenseStatus: filters.status,
          benefitId:
            filters.source?.filter(
              (source) => source !== RECOGNITION_SHOP_ID,
            ) ?? [],
          recognition:
            filters.source?.includes(RECOGNITION_SHOP_ID) || undefined,
        },
      },
    });
  },
  createExpense: ({ employeeId, expense }: CreateExpense) => {
    return api({
      url: Endpoints.clientAdmin.employees.byId.expenses.root.replace(
        ':employeeId',
        employeeId,
      ),
      method: 'post',
      data: {
        date: expense.date,
        status: expense.status,
        sourceBenefitIds: expense.benefits,
        sourceRecognition: expense.recognition,
        marketplaceItemCategories: expense.shopCategories,
        expense: toCents(expense.amount),
        description: expense.description,
      },
    });
  },
  getWalletBenefits: ({ employeeId, filters }: GetWalletBenefits) => {
    return api<TableDataResponse<IEmployeeWalletBenefit>>({
      url: Endpoints.clientAdmin.employees.byId.walletBenefits.root.replace(
        ':employeeId',
        employeeId,
      ),
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        filter: {
          benefitIds: filters.benefits?.filter(Boolean),
          status: filters.status,
        },
      },
    });
  },
  getWalletRecognitions: ({ employeeId }: GetWalletRecognitions) => {
    return api<IEmployeeWalletRecognitions>({
      url: Endpoints.clientAdmin.employees.byId.walletRecognitions.root.replace(
        ':employeeId',
        employeeId,
      ),
    });
  },
  unlinkEmployee: ({ employeeId }: UnlinkEmployee) => {
    return api({
      url: Endpoints.clientAdmin.employees.byId.unlink.replace(
        ':employeeId',
        employeeId,
      ),
      method: 'post',
    });
  },
  importConsents: ({ file }: ImportConsents) => {
    return api<IFileImport>({
      url: Endpoints.clientAdmin.employees.consents.import.xls,
      method: 'post',
      data: getFormData([{ field: 'file', value: file }]),
    });
  },
  exportConsents: ({ filters }: ExportConsents) => {
    const { page, pageSize, sorting, direction, ...restFilters } =
      getFilters(filters);
    return api({
      url: Endpoints.clientAdmin.employees.consents.export.xls,
      params: {
        ...restFilters,
      },
      ...exportXlsxConfig,
    });
  },
  prepareEmployeeBlueprint: ({
    file,
    identifierColumns,
  }: PrepareEmployeeBlueprint) => {
    return api<{ jobId: string }>({
      url: Endpoints.clientAdmin.employees.export.blueprint.prepare,
      method: 'post',
      data: getFormData([
        { field: 'file', value: file },
        { field: 'identifierColumns', value: identifierColumns },
      ]),
    });
  },
  importEmployeeBlueprint: ({ file }: ImportEmployeeBlueprint) => {
    return api<{ jobId: string }>({
      url: Endpoints.clientAdmin.employees.import.blueprint,
      method: 'post',
      data: getFormData([{ field: 'file', value: file }]),
    });
  },
  exportOrgStructureEmployees: ({ filters }: ExportOrgStructureEmployees) => {
    const { page, pageSize, sorting, direction, ...restFilters } =
      getFilters(filters);
    return api<{ jobId: string; queue: string }>({
      url: Endpoints.clientAdmin.employees.orgStructure.export.xls,
      params: {
        ...restFilters,
      },
    });
  },
  exportCompanyOrgStructure: () => {
    return api<{ jobId: string; queue: string }>({
      url: Endpoints.clientAdmin.companies.orgStructure.export.xls,
    });
  },
  importOrgStructure: ({ file }: ImportOrgStructure) => {
    return api<{ jobId: string; queue: string }>({
      url: Endpoints.clientAdmin.employees.orgStructure.import.xls,
      method: 'post',
      data: getFormData([{ field: 'file', value: file }]),
    });
  },
};
